import './index.css'
import { RedwoodReactQueryProvider } from './react-query'
import theme from './theme'
import DateAdapter from '@mui/lab/AdapterDayjs'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AuthProvider, useAuth } from '@redwoodjs/auth'
import { Head, RedwoodProvider } from '@redwoodjs/web'
import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import { useEffect } from 'react'
import Routes from 'src/Routes'
import { currentEnvironment } from 'src/lib/env'
import 'src/lib/fetch'
import * as _mixpanel from 'src/lib/mixpanel'
import FatalErrorPage from 'src/pages/FatalErrorPage'

Sentry.init({
  dsn: process.env.SENTRY_DSN_WEB,
  integrations: [new BrowserTracing()],
  tracesSampleRate: 0.2,
  environment: currentEnvironment,
  enabled: currentEnvironment !== 'LOCAL', // disabled when running locally
  release: process.env.RELEASE_VERSION,
})

const SetSentryUser = () => {
  const { currentUser } = useAuth()

  useEffect(() => {
    if (currentUser) {
      const { id, username, email } = currentUser as any
      Sentry.setUser({ id, username, email })
    } else {
      Sentry.configureScope((scope) => scope.setUser(null))
    }
  }, [currentUser])

  return <></>
}

const manifestFilePath =
  currentEnvironment === 'PROD'
    ? '/manifest.json'
    : `/manifest.${currentEnvironment.toLowerCase()}.json`

const appIconFilePath =
  currentEnvironment === 'PROD'
    ? '/majr-icon.png'
    : `/majr-icon.${currentEnvironment.toLowerCase()}.png`

const App = () => (
  <Sentry.ErrorBoundary fallback={FatalErrorPage}>
    <RedwoodProvider titleTemplate="%AppTitle">
      <Head>
        <link rel="icon" type="image/png" href={appIconFilePath} />
        <link rel="manifest" href={manifestFilePath}></link>
        {/* https://developer.apple.com/library/archive/documentation/AppleApplications/Reference/SafariHTMLRef/Articles/MetaTags.html */}
        <link rel="apple-touch-icon" href={appIconFilePath}></link>
        <meta name="apple-mobile-web-app-capable" content="yes"></meta>
        <meta name="apple-mobile-web-app-title" content="majr"></meta>
        <meta name="theme-color" content={theme.palette.secondary.light} />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black"
        ></meta>
        <link rel="apple-touch-startup-image" href="/images/launch.webp"></link>
      </Head>
      <LocalizationProvider dateAdapter={DateAdapter}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider type="dbAuth">
            <SetSentryUser />
            <RedwoodReactQueryProvider>
              <Routes />
            </RedwoodReactQueryProvider>
          </AuthProvider>
        </ThemeProvider>
      </LocalizationProvider>
    </RedwoodProvider>
  </Sentry.ErrorBoundary>
)

export default App
